// Monthly streaming report index collection.
import { defineCollection } from '../schema';
export const reportsCollection = defineCollection({
    id: 'collection:reports',
    kind: 'index',
    route: '/party-central/reports',
    title: 'Reports',
    description: 'Data-driven monthly reports on party game streaming activity on Twitch.',
    seo: {
        title: 'Party Central Monthly Twitch Reports',
        description: 'Read monthly Twitch party-game streaming reports with rankings, trends, methodology, and insights for game developers, streamers, viewers, and analysts.',
    },
    hero: {
        eyebrow: [
            {
                type: 'text',
                text: 'Party Central',
            },
        ],
        title: [
            {
                type: 'text',
                text: 'Reports',
            },
        ],
        showDescription: true,
        description: [
            {
                type: 'text',
                text: 'The authoritative source on party games for streamers.',
            },
        ],
        background: 'gradient-card',
        backgroundAccentFamily: 'lime',
    },
    sections: [
        {
            id: 'intro',
            title: [
                {
                    type: 'text',
                    text: 'Overview',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Every month, Airhead Games publishes a data-driven look at the party game streaming landscape on Twitch. These reports track which games are pulling in streamers and viewers, which streamers are leading the party game space, and how activity breaks down across languages, days of the week, and more.',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Whether you are a game developer researching your audience, a streamer looking for your next game to feature, a viewer hunting for the best party game content, or an industry analyst tracking genre trends, these reports give you the insights you need. New reports are published monthly, covering the previous calendar month.',
                        },
                    ],
                },
            ],
        },
        {
            id: 'most-recent-reports',
            title: [
                {
                    type: 'text',
                    text: 'Most Recent Reports',
                },
            ],
            widgets: [
                {
                    type: 'documentList',
                    source: {
                        type: 'query',
                        kind: 'report',
                        sortBy: 'publishedAt-desc',
                    },
                    layout: 'grid',
                },
            ],
        },
        {
            id: 'methodology',
            title: [
                {
                    type: 'text',
                    text: 'Methodology',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Each report is built using data collected from IGDB and Twitch. We use IGDB as the source of truth for game information, and Twitch for stream data.',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'strong',
                                children: [
                                    {
                                        type: 'text',
                                        text: 'Party Game Capture',
                                    },
                                ],
                            },
                            {
                                type: 'text',
                                text: ': We poll the IGDB (Internet Game Database) approximately once per month to build a complete list of party games. We rely on IGDB for game data such as release date and genre tags. Expectations may differ on what should or should not be called a party game; however, we consider IGDB the source of truth. Our approach is consistent with how Twitch handles browsing by game category.',
                            },
                        ],
                        [
                            {
                                type: 'strong',
                                children: [
                                    {
                                        type: 'text',
                                        text: 'Stream Capture',
                                    },
                                ],
                            },
                            {
                                type: 'text',
                                text: ': We poll the Twitch API approximately every 3.5 hours for streams playing party games. The spacing of our polling helps create a representative sample of hour/days over the course of a month and takes into consideration typical stream durations. In our data, each poll is called a "capture event" and we observe every live stream\'s game, streamer, viewer count, and language.',
                            },
                        ],
                        [
                            {
                                type: 'strong',
                                children: [
                                    {
                                        type: 'text',
                                        text: 'Game Party Score (0–100)',
                                    },
                                ],
                            },
                            {
                                type: 'text',
                                text: ': A proprietary percentile-ranked composite of three metrics, including Streamer Breadth (how widely a game is played), Streamer Dedication (how often streamers return to a game), and Audience Draw (how many viewers a game has on average). Scores are percentile-ranked, so 96 means the game outperforms 96% of all ranked games on the composite.',
                            },
                        ],
                        [
                            {
                                type: 'strong',
                                children: [
                                    {
                                        type: 'text',
                                        text: 'Streamer Party Score (0–100)',
                                    },
                                ],
                            },
                            {
                                type: 'text',
                                text: ': A proprietary percentile-ranked composite of three metrics, including Consistency (how often the streamer was live), Audience Size (how many viewers the streamer has on average), and Variety (how many unique games the streamer has played). Scores are percentile-ranked, so 96 means the streamer outperforms 96% of all ranked streamers on the composite.',
                            },
                        ],
                        [
                            {
                                type: 'strong',
                                children: [
                                    {
                                        type: 'text',
                                        text: 'Minimum Thresholds',
                                    },
                                ],
                            },
                            {
                                type: 'text',
                                text: ': Some rankings apply minimum thresholds to filter out low-sample-size noise. For example, a minimum number of streams or viewers. Thresholds are noted in each table where they apply.',
                            },
                        ],
                    ],
                },
            ],
        },
    ],
});
//# sourceMappingURL=reports.js.map